import {
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery,
    OutlinedInput,
    Box,
    Avatar
} from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import MainCard from 'ui-component/cards/MainCard';
import CircularLoader from 'ui-component/CircularLoader';
import Transitions from 'ui-component/extended/Transitions';
import { useTheme, styled } from '@mui/material/styles';
import { shouldForwardProp } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import menuItems from 'menu-items';
import PropTypes from 'prop-types';
import { bindToggle } from 'material-ui-popup-state';
import { getAllClients } from 'api/clients';
import SearchResultList from './SearchResultList';

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 434,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important'
    },
    [theme.breakpoints.down('lg')]: {
        width: 250
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 4,
        background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff'
    }
}));

const GlobalSearch = ({ viewPort = '', popupState }) => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const [value, setValue] = useState('');
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);
    const [isSearching, setIsSearching] = useState(false);
    const [filterMenus, setFilterMenus] = useState({
        navigations: [],
        deployments: []
    });

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleSearch = async (value) => {
        // prevent search while search length is not 3 and above
        setValue(value);
        if (value?.length < 3) {
            setOpen(false);
            return;
        }

        let navigationMenus = [];
        let deploymentMenus = [];
        try {
            setIsSearching(true);
            setOpen(value !== '' ? true : false);

            // For Navigation Menus
            let dynamicPattern = new RegExp(value, 'i');
            for (let menu of menuItems?.items || []) {
                // filter in menu title
                if (dynamicPattern.test(menu.title) && typeof menu?.children === 'undefined') {
                    navigationMenus.push(menu);
                } else {
                    navigationMenus = [...navigationMenus, ...handleFilterTags(dynamicPattern, menu)];
                }
            }

            // For Client Menus
            const result = await getAllClients({
                query: value,
                sortField: 'displayName',
                sorting: 1,
                filterDisplayNameOnly: true
            });

            const deployments = result?.data?.data || [];
            // prepare here the menu
            for (let deployment of deployments) {
                deploymentMenus.push({
                    title: deployment?.displayName || '',
                    subtitle: deployment?.deployment || '',
                    url: `/deployments?search=${deployment?.deployment}`
                });
            }
        } catch (e) {
            console.log('e', e);
        } finally {
            setFilterMenus({
                ...filterMenus,
                navigations: navigationMenus,
                deployments: deploymentMenus
            });
            setIsSearching(false);
        }
    };

    const handleFilterTags = (dynamicPattern, menu) => {
        const filterMenus = [];
        /**
         * Type of where can find tags
         * 1. is in the main prop
         * 2. if has children
         * 3. if has dynamicPages
         * 4. if has children and has dynamicPages
         */

        if (menu?.tags) {
            for (let tag of menu?.tags || []) {
                // filter in tags
                if (dynamicPattern.test(tag)) {
                    filterMenus.push(menu);
                    break;
                }
            }
        }

        if (menu?.children) {
            for (let child of menu.children) {
                if (child?.dynamicPages) {
                    for (let page of child.dynamicPages) {
                        for (let tag of page?.tags || []) {
                            // filter in tags
                            if (dynamicPattern.test(tag)) {
                                page = {
                                    ...page,
                                    url: `${child?.url}${page?.urlParams ? `?page=${page?.urlParams}` : ''}`
                                };
                                filterMenus.push(page);
                                break;
                            }
                        }
                    }
                } else {
                    for (let tag of child.tags || []) {
                        // filter in tags
                        if (dynamicPattern.test(tag)) {
                            filterMenus.push(child);
                            break;
                        }
                    }
                }
            }
        }

        if (menu?.dynamicPages) {
            for (let page of menu.dynamicPages) {
                for (let tag of page?.tags || []) {
                    // filter in tags
                    if (dynamicPattern.test(tag)) {
                        page = {
                            ...page,
                            url: `${menu?.url}${page?.urlParams ? `?page=${page?.urlParams}` : ''}`
                        };
                        filterMenus.push(page);
                        break;
                    }
                }
            }
        }
        return filterMenus;
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    return (
        <>
            {viewPort === 'web' ? (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            ) : (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position="end">
                            <Box sx={{ ml: 2 }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        ...theme.typography.commonAvatar,
                                        ...theme.typography.mediumAvatar,
                                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.orange.light,
                                        color: theme.palette.orange.dark,
                                        '&:hover': {
                                            background: theme.palette.orange.dark,
                                            color: theme.palette.orange.light
                                        }
                                    }}
                                    {...bindToggle(popupState)}
                                >
                                    <IconX stroke={1.5} size="20px" />
                                </Avatar>
                            </Box>
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            )}
            <Popper
                placement="bottom-start"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 10]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper sx={{ width: anchorRef.current.offsetWidth }}>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column">
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                    <Grid item>
                                                        <Stack direction="row" spacing={2}>
                                                            <Typography variant="subtitle1">Search Results</Typography>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} p={0}>
                                                <Divider sx={{ my: 0 }} />
                                            </Grid>
                                            {isSearching ? (
                                                <Grid container spacing={1} sx={{ px: 2, py: 1.5 }}>
                                                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                        <CircularLoader size={24} minHeight="0rem" />
                                                    </Grid>
                                                </Grid>
                                            ) : filterMenus?.navigations?.length == 0 && filterMenus?.deployments?.length == 0 ? (
                                                <Grid container spacing={1} sx={{ px: 2, py: 1.5 }}>
                                                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                        No results
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                <>
                                                    {filterMenus?.navigations?.length > 0 && (
                                                        <SearchResultList header="Navigation" list={filterMenus?.navigations} />
                                                    )}

                                                    {filterMenus?.deployments?.length > 0 && (
                                                        <SearchResultList header="Deployment" list={filterMenus?.deployments} />
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

GlobalSearch.propTypes = {
    viewPort: PropTypes.string,
    popupState: PropTypes.object
};

export default GlobalSearch;
